import React, { useState, useEffect } from 'react';
import { Link24Regular } from '@fluentui/react-icons';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useStandalonePwa } from '../hooks/useStandalonePwa';
import './ProjectDetails.css';

function ProjectDetails({ projectData }) {
  const { authFetch, isReady } = useAuthFetch();
  const isStandalonePwa = useStandalonePwa();
  const [financialData, setFinancialData] = useState({
    originalContractAmount: 0,
    approvedContractCOs: 0,
    currentContractAmount: 0,
    potentialCOsAmount: 0,
  });
  const [showCopiedToast, setShowCopiedToast] = useState(false);

  const generateShareableLink = () => {
    if (projectData.opportunityNo) {
      const baseUrl = window.location.origin;
      return `${baseUrl}/project/${projectData.opportunityNo}`;
    }
    return null;
  };

  const handleCopyShareLink = (e) => {
    e.stopPropagation();
    const shareLink = generateShareableLink();
    if (shareLink) {
      navigator.clipboard.writeText(shareLink);
      setShowCopiedToast(true);
      setTimeout(() => setShowCopiedToast(false), 1500);
    }
  };

  useEffect(() => {
    const fetchFinancialData = async () => {
      if (!projectData.projectId || !projectData.division || !isReady) return;

      try {
        const response = await authFetch(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/finicial-budget?projectID=${projectData.projectId}&divisionName=${projectData.division}`
        );
        const data = await response.json();

        const findValue = (label) => {
          const item = data.budgetProgressData.find((item) => item.label === label);
          return item ? item.value : 0;
        };

        setFinancialData({
          originalContractAmount: findValue('Original Contract Amount'),
          approvedContractCOs: findValue('Approved Contract COs'),
          currentContractAmount: findValue('Current Contract Amount'),
          potentialCOsAmount: findValue('Potential COs Amount'),
        });
      } catch (error) {
        console.error('Error fetching financial data:', error);
      }
    };

    fetchFinancialData();
  }, [projectData.projectId, projectData.division, authFetch, isReady]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const financialRows = (
    <>
      <div className="flex justify-between items-center mb-[0.35rem] max-md:mb-2">
        <div className="text-zinc-800 text-sm max-md:text-xs">Org. Contract Amt:</div>
        <div className="text-zinc-800 text-sm max-md:text-xs">{formatCurrency(financialData.originalContractAmount)}</div>
      </div>
      <div className="flex justify-between items-center mb-[0.35rem] max-md:mb-2">
        <div className="text-zinc-800 text-sm max-md:text-xs">Approved CO Amt:</div>
        <div className="text-zinc-800 text-sm max-md:text-xs">{formatCurrency(financialData.approvedContractCOs)}</div>
      </div>
      <div className="h-px bg-gray-200 my-3 max-md:my-2" />
      <div className="flex justify-between items-center mb-3 max-md:mb-2 font-bold">
        <div className="text-zinc-800 text-sm max-md:text-xs">Current Contract Amt:</div>
        <div className="text-zinc-800 text-sm max-md:text-xs">{formatCurrency(financialData.currentContractAmount)}</div>
      </div>
      <div className="flex justify-between items-center italic">
        <div className="text-zinc-800 text-sm max-md:text-xs">Potential CO Amt:</div>
        <div className="text-zinc-800 text-sm max-md:text-xs">{formatCurrency(financialData.potentialCOsAmount)}</div>
      </div>
    </>
  );

  if (isStandalonePwa) {
    return (
      <main className="project-details-pwa">
        <section className="project-details-pwa__card">
          <div className="project-details-pwa__name-block">
            <div className="project-details-pwa__label">Project Name:</div>
            <div className="project-details-pwa__name-value">
              <span
                className="project-details-pwa__name-text"
                title={projectData.projectName || '-'}
              >
                {projectData.projectName || '-'}
              </span>
              <button
                type="button"
                onClick={handleCopyShareLink}
                className="project-details-pwa__link-btn"
                title="Copy shareable link"
                aria-label="Copy shareable link"
              >
                <Link24Regular />
              </button>
              {showCopiedToast && (
                <span className="project-details-pwa__toast">Link Copied!</span>
              )}
            </div>
          </div>

          <div className="project-details-pwa__divider" />

          <div className="project-details-pwa__grid">
            <div>
              <div className="project-details-pwa__label">Branch:</div>
              <div
                className="project-details-pwa__field-value"
                title={projectData.company || '-'}
              >
                {projectData.company || '-'}
              </div>
            </div>
            <div>
              <div className="project-details-pwa__label">Scope:</div>
              <div
                className="project-details-pwa__field-value"
                title={projectData.scope || '-'}
              >
                {projectData.scope || '-'}
              </div>
            </div>
            <div>
              <div className="project-details-pwa__label">Project No:</div>
              <div
                className="project-details-pwa__field-value"
                title={projectData.projectNo || '-'}
              >
                {projectData.projectNo || '-'}
              </div>
            </div>
            <div>
              <div className="project-details-pwa__label">Opportunity No:</div>
              <div
                className="project-details-pwa__field-value"
                title={projectData.opportunityNo || '-'}
              >
                {projectData.opportunityNo || '-'}
              </div>
            </div>
          </div>

          <div className="project-details-pwa__field--full">
            <div className="project-details-pwa__label">Delivery Method:</div>
            <div
              className="project-details-pwa__field-value"
              title={projectData.deliveryMethod || '-'}
            >
              {projectData.deliveryMethod || '-'}
            </div>
          </div>
        </section>

        <aside className="project-details-pwa__financials">
          <div className="project-details-pwa__financial-row">
            <span>Org. Contract Amt:</span>
            <span>{formatCurrency(financialData.originalContractAmount)}</span>
          </div>
          <div className="project-details-pwa__financial-row">
            <span>Approved CO Amt:</span>
            <span>{formatCurrency(financialData.approvedContractCOs)}</span>
          </div>
          <div className="project-details-pwa__financial-divider" />
          <div className="project-details-pwa__financial-row project-details-pwa__financial-row--bold">
            <span>Current Contract Amt:</span>
            <span>{formatCurrency(financialData.currentContractAmount)}</span>
          </div>
          <div className="project-details-pwa__financial-row project-details-pwa__financial-row--italic">
            <span>Potential CO Amt:</span>
            <span>{formatCurrency(financialData.potentialCOsAmount)}</span>
          </div>
        </aside>
      </main>
    );
  }

  return (
    <main>
      <div className="flex gap-4 max-md:flex-col">
        {/* Project Details Section */}
        <section className="flex-1 max-md:w-full bg-[#e5e7eb] p-6 max-md:p-4 rounded-lg shadow-[0_2px_4px_rgba(0,0,0,0.1)]">
          <div className="flex flex-col">
            {/* First Row - Project Name (75%) and Branch (25%) */}
            <div className="flex max-md:flex-col">
              {/* Project Name - 75% */}
              <div className="relative group w-[70%] max-md:w-full pr-4 max-md:pr-0 max-md:mb-4">
                <div className="text-gray-600 mb-1 text-sm max-md:text-xs">Project Name:</div>
                <div className="text-lg max-md:text-base text-red-700 font-medium truncate" title={projectData.projectName || '-'} style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}>
                  {projectData.projectName || '-'}
                  <button
                    type="button"
                    onClick={handleCopyShareLink}
                    className="text-gray-500 hover:text-blue-600 transition-colors"
                    title="Copy shareable link"
                    style={{ marginLeft: '8px', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                  >
                    <Link24Regular />
                  </button>
                  {showCopiedToast && (
                    <span style={{
                      marginLeft: '8px',
                      background: '#222',
                      color: '#fff',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '14px',
                      zIndex: 2,
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                    }}
                    >
                      Link Copied!
                    </span>
                  )}
                </div>
                <div className="invisible group-hover:visible absolute z-10 bg-gray-900 text-white p-2 rounded text-sm whitespace-normal max-w-xs -translate-y-full top-0">
                  {projectData.projectName || '-'}
                </div>
                <div className="absolute right-0 top-0 h-full w-px bg-gray-400 max-md:hidden" />
              </div>

              {/* Branch - 25% */}
              <div className="relative group w-[30%] max-md:w-full pl-4 max-md:pl-0">
                <div className="text-gray-600 mb-1 text-sm max-md:text-xs">Branch:</div>
                <div className="text-lg max-md:text-base text-zinc-800 truncate" title={projectData.company || '-'}>
                  {projectData.company || '-'}
                </div>
                <div className="invisible group-hover:visible absolute z-10 bg-gray-900 text-white p-2 rounded text-sm whitespace-normal max-w-xs -translate-y-full top-0">
                  {projectData.company || '-'}
                </div>
              </div>
            </div>

            <div className="h-px bg-gray-400 my-4 max-md:my-3" />

            {/* Second Row */}
            <div className="flex max-md:flex-col max-md:gap-4">
              {/* Scope - 35% */}
              <div className="relative group w-[35%] max-md:w-full pr-4 max-md:pr-0">
                <div className="text-gray-600 mb-1 text-sm max-md:text-xs">Scope:</div>
                <div className="text-lg max-md:text-base text-zinc-800 truncate" title={projectData.scope || '-'}>
                  {projectData.scope || '-'}
                </div>
                <div className="invisible group-hover:visible absolute z-10 bg-gray-900 text-white p-2 rounded text-sm whitespace-normal max-w-xs -translate-y-full top-0">
                  {projectData.scope || '-'}
                </div>
                <div className="absolute right-0 top-0 h-full w-px bg-gray-400 max-md:hidden" />
              </div>

              {/* Delivery Method - 35% */}
              <div className="relative group w-[35%] max-md:w-full px-4 max-md:px-0">
                <div className="text-gray-600 mb-1 text-sm max-md:text-xs">Delivery Method:</div>
                <div className="text-lg max-md:text-base text-zinc-800 truncate" title={projectData.deliveryMethod || '-'}>
                  {projectData.deliveryMethod || '-'}
                </div>
                <div className="invisible group-hover:visible absolute z-10 bg-gray-900 text-white p-2 rounded text-sm whitespace-normal max-w-xs -translate-y-full top-0">
                  {projectData.deliveryMethod || '-'}
                </div>
                <div className="absolute right-0 top-0 h-full w-px bg-gray-400 max-md:hidden" />
              </div>

              {/* Project No - 15% */}
              <div className="relative group w-[15%] max-md:w-full px-4 max-md:px-0">
                <div className="text-gray-600 mb-1 text-sm max-md:text-xs">Project No:</div>
                <div className="text-lg max-md:text-base text-zinc-800 truncate" title={projectData.projectNo || '-'}>
                  {projectData.projectNo || '-'}
                </div>
                <div className="invisible group-hover:visible absolute z-10 bg-gray-900 text-white p-2 rounded text-sm whitespace-normal max-w-xs -translate-y-full top-0">
                  {projectData.projectNo || '-'}
                </div>
                <div className="absolute right-0 top-0 h-full w-px bg-gray-400 max-md:hidden" />
              </div>

              {/* Opportunity No - 15% */}
              <div className="relative group w-[15%] max-md:w-full pl-4 max-md:pl-0">
                <div className="text-gray-600 mb-1 text-sm max-md:text-xs">Opportunity No:</div>
                <div className="text-lg max-md:text-base text-zinc-800 truncate" title={projectData.opportunityNo || '-'}>
                  {projectData.opportunityNo || '-'}
                </div>
                <div className="invisible group-hover:visible absolute z-10 bg-gray-900 text-white p-2 rounded text-sm whitespace-normal max-w-xs -translate-y-full top-0">
                  {projectData.opportunityNo || '-'}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Financials Section */}
        <aside className="w-1/3 max-md:w-full bg-white p-6 max-md:p-4 rounded-lg">
          <div className="flex flex-col">
            {financialRows}
          </div>
        </aside>
      </div>
    </main>
  );
}

export default ProjectDetails;
